import React, { useCallback, useEffect } from "react";
import ScrollableImageList from "../common/ScrollableImageList/ScrollableImageList";
import {
  Box,
  Typography,
  Paper,
  SvgIcon,
  CircularProgress,
} from "@material-ui/core";
import { ReactComponent as refreshIconNew } from "./../assets/icons/refresh-icon-new.svg";
import useStyles from "./POD.styles";
import { useDispatch, useSelector } from "react-redux";
import { fetchPOD } from "./redux/PODSlice";
import { AppState } from "config/redux/reducers";
import Image from "common/Image";

interface PODComponentProps {
  consignmentCode: string;
  comment?: string;
  type: "std" | "exp";
}

const SectionLoader = () => (
  <Box
    position="absolute"
    top={0}
    left={0}
    width="100%"
    height="100%"
    zIndex={10}
    display="flex"
    alignItems="center"
    justifyContent="center"
    bgcolor="rgba(255,255,255,0.7)"
  >
    <CircularProgress />
  </Box>
);

const EmptyBox = ({ label }: { label: string }) => (
  <Box
    style={{
      height: 100,
      background: "#f0f0f0",
      borderRadius: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 2,
    }}
  >
    <Typography color="textSecondary" style={{ fontSize: 14 }}>
      {label} not available
    </Typography>
  </Box>
);

const InfoPaper = React.memo<{ title: string; children: React.ReactNode }>(
  ({ title, children }) => {
    const classes = useStyles();
    return (
      <Paper elevation={0} className={classes.paper}>
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        {children}
      </Paper>
    );
  }
);

const PODComponent: React.FC<PODComponentProps> = React.memo(
  ({ comment = "", consignmentCode, type }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { PODImages: images, PODSign: sign, loading } = useSelector(
      (state: AppState) => state.pod
    );

    const handlePodClick = useCallback(() => {
      if (consignmentCode) {
        dispatch(
          fetchPOD({
            params: consignmentCode,
            type: type,
          })
        );
      }
    }, [dispatch]);

    useEffect(() => {
      if (consignmentCode) {
        handlePodClick();
      }
    }, [consignmentCode, dispatch]);

    return (
      <Box position="relative">
        {loading && <SectionLoader />}
        <Box className={classes.headerRow}>
          <Typography variant="subtitle2">Images ({images.length})</Typography>
          <SvgIcon
            className={classes.icon}
            onClick={handlePodClick}
            component={refreshIconNew}
          />
        </Box>
        {images.length === 0 ? (
          <EmptyBox label="Image" />
        ) : (
          <ScrollableImageList
            images={images}
            imageWidth={94}
            imageHeight={122}
          />
        )}
        <Box className={classes.infoRow}>
          <InfoPaper title="Customer Signature">
            {!sign ? (
              <EmptyBox label="Signature" />
            ) : (
              <Box className={classes.signatureBox}>
                <Image 
                  isProtected
                  src={sign}
                  alt="Signature"
                  style={{ height: "100%", borderRadius: 8, maxWidth: "100%" }}
                />
              </Box>
            )}
          </InfoPaper>
          <InfoPaper title="Comments">
            {!comment ? (
              <EmptyBox label="Comment" />
            ) : (
              <Box className={classes.commentBox} width="100%">
                <Typography variant="body2">{comment}</Typography>
              </Box>
            )}
          </InfoPaper>
        </Box>
      </Box>
    );
  }
);

export default PODComponent;
